import {
  type ActionCreator,
  createAction,
} from '@packages/utilities/create-action';
import type {BreakpointID} from '@packages/systems/style/types';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import type {MCCGuardrailsPolicyType} from './guardrails';

export const PAGE_VIEW_ON_OPEN_CANVAS_OPENED =
  'PAGE_VIEW_ON_OPEN_CANVAS_OPENED' as const;
export const PAGE_VIEW_ON_OPEN_CANVAS_CLOSED =
  'PAGE_VIEW_ON_OPEN_CANVAS_CLOSED' as const;
export const MCC_FRAME_SELECTED = 'MCC_FRAME_SELECTED' as const;
export const MCC_FRAME_DESELECTED = 'MCC_FRAME_DESELECTED' as const;
export const MCC_FRAME_WIDTH_CHANGED = 'MCC_FRAME_WIDTH_CHANGED' as const;
export const MCC_FRAME_VIEWPORT_HEIGHT_CHANGED =
  'MCC_FRAME_VIEWPORT_HEIGHT_CHANGED' as const;
export const MCC_FRAME_WIDTH_SYNCED_TO_CANVAS =
  'MCC_FRAME_WIDTH_SYNCED_TO_CANVAS' as const;
export const MCC_NODE_CLICKED_IN_INACTIVE_FRAME =
  'MCC_NODE_CLICKED_IN_INACTIVE_FRAME' as const;
export const MCC_FRAME_CONTENT_REVEALED = 'MCC_FRAME_CONTENT_REVEALED' as const;
export const MCC_FRAME_CONTENT_HIDDEN = 'MCC_FRAME_CONTENT_HIDDEN' as const;
export const MCC_GUARDRAILS_EVALUATED = 'MCC_GUARDRAILS_EVALUATED' as const;

export {
  EXISTING_BREAKPOINT_SELECTED,
  NEW_BREAKPOINT_SELECTED,
} from '@packages/systems/designer/actions';

/**
 * How MCC was entered. Exported so telemetry consumers can narrow
 * `action.payload.source` instead of comparing bare string literals.
 */
export type PageViewOnOpenCanvasOpenedSource =
  | 'Multi context toggle'
  | 'Persisted preference restore';

export type PageViewOnOpenCanvasOpenedPayload = {
  breakpointId: BreakpointID;
  canvasWidth: number;
  /**
   * How MCC was entered. Distinguishes intentional canvas-bar opens from
   * site-load preference restore so analytics and open-timing telemetry can
   * diverge while store consumers still share one open action.
   */
  source: PageViewOnOpenCanvasOpenedSource;
};

export const pageViewOnOpenCanvasOpened: ActionCreator<
  typeof PAGE_VIEW_ON_OPEN_CANVAS_OPENED,
  PageViewOnOpenCanvasOpenedPayload
> = createAction(PAGE_VIEW_ON_OPEN_CANVAS_OPENED);
export const pageViewOnOpenCanvasClosed: ActionCreator<
  typeof PAGE_VIEW_ON_OPEN_CANVAS_CLOSED,
  {source: 'Multi context toggle' | 'Frame expand button'}
> = createAction(PAGE_VIEW_ON_OPEN_CANVAS_CLOSED);

export const mccFrameSelected: ActionCreator<
  typeof MCC_FRAME_SELECTED,
  {frameId: string}
> = createAction(MCC_FRAME_SELECTED);

export const mccFrameDeselected: ActionCreator<
  typeof MCC_FRAME_DESELECTED,
  void
> = createAction(MCC_FRAME_DESELECTED);

export const mccFrameWidthChanged: ActionCreator<
  typeof MCC_FRAME_WIDTH_CHANGED,
  {
    frameId: string;
    width: number | null;
  }
> = createAction(MCC_FRAME_WIDTH_CHANGED);

export const mccFrameViewportHeightChanged: ActionCreator<
  typeof MCC_FRAME_VIEWPORT_HEIGHT_CHANGED,
  {
    frameId: string;
    height: number | null;
  }
> = createAction(MCC_FRAME_VIEWPORT_HEIGHT_CHANGED);

export const mccFrameWidthSyncedToCanvas: ActionCreator<
  'MCC_FRAME_WIDTH_SYNCED_TO_CANVAS',
  {
    width: number;
  }
> = createAction(MCC_FRAME_WIDTH_SYNCED_TO_CANVAS);

export const mccNodeClickedInInactiveFrame: ActionCreator<
  typeof MCC_NODE_CLICKED_IN_INACTIVE_FRAME,
  {
    frameId: string;
    selectedNodeNativeIdPath: NativeId[];
  }
> = createAction(MCC_NODE_CLICKED_IN_INACTIVE_FRAME);

/** Attribution for a frame visibility toggle. Carried for analytics only. */
export type MCCFrameVisibilitySource = 'canvas' | 'navigator';

export const mccFrameContentRevealed: ActionCreator<
  typeof MCC_FRAME_CONTENT_REVEALED,
  {frameId: string; source: MCCFrameVisibilitySource}
> = createAction(MCC_FRAME_CONTENT_REVEALED);

/**
 * Hides a frame's page content, returning it to the placeholder state.
 */
export const mccFrameContentHidden: ActionCreator<
  typeof MCC_FRAME_CONTENT_HIDDEN,
  {frameId: string; source: MCCFrameVisibilitySource}
> = createAction(MCC_FRAME_CONTENT_HIDDEN);

/**
 * Dispatched by `MCCGuardrailsEngine` once it has a guardrail policy for
 * the current page — either freshly measured (one frame's DOM count × the
 * frame count, classified against the total-node thresholds) or read from
 * the context-matched session cache. The reducer stores the tier (and the
 * page/locale/frame-set cache entry), reveals the default frame set for the tier
 * anchored on `activeFrameId`, and releases the page-switch overlay — all
 * in one dispatch, so the frames mount and the overlay drops in the same
 * commit.
 */
export const mccGuardrailsEvaluated: ActionCreator<
  typeof MCC_GUARDRAILS_EVALUATED,
  {
    pageId: string | null;
    localeId: string | null;
    activeFrameId: string;
    enabledFrameIds: readonly string[];
    policyType: MCCGuardrailsPolicyType;
    /**
     * Estimated total rendered nodes behind `policyType`. Undefined when
     * measurement timed out, so telemetry can report an unmeasured page
     * instead of a page with zero nodes.
     */
    totalNodeCount?: number;
  }
> = createAction(MCC_GUARDRAILS_EVALUATED);
